// src/components/ui/range-calendar.tsx
// IR-1384 r2 (Rotem, 2026-08-12): "smaller calendar", "white highlights
// actually block the dates", "I don't see the selected start and end date",
// "use IRIS controller". The calendar half of every OPS date filter now lives
// here: the compact dark month grid (themed via .iris-daypicker) plus the two
// endpoint boxes that IRIS's own range picker shows above its calendar, so
// what you have picked so far is readable as text and not only as shading.
// Shared by the Groups "Added" filter and the Join Links range picker so the
// two controls stay the same control.
import { cn } from "@/lib/cn";
import { format } from "date-fns";
import { type DateRange, DayPicker } from "react-day-picker";
import "react-day-picker/style.css";

/** Human-readable endpoint — the ISO strings stay on the wire, not on screen. */
const DISPLAY = "d MMM yyyy";

export function formatEndpoint(day: Date | undefined): string {
  return day ? format(day, DISPLAY) : "—";
}

/**
 * Which endpoint the next click lands on, mirroring react-day-picker's own
 * range behaviour: nothing picked or a complete range starts over at the
 * start, a half-open range is waiting for the end.
 */
export function nextEndpoint(draft: DateRange | undefined): "start" | "end" {
  if (!draft?.from) return "start";
  return draft.to ? "start" : "end";
}

function Endpoint({
  label,
  day,
  active,
}: {
  label: string;
  day: Date | undefined;
  active: boolean;
}) {
  return (
    <div
      className={cn(
        "flex-1 rounded-md border px-2 py-1",
        active
          ? "border-[var(--iris-neon-cyan)] bg-[rgba(34,211,255,0.08)]"
          : "border-[var(--iris-line-2)] bg-[var(--iris-bg-input)]",
      )}
    >
      <div className="text-[9px] uppercase tracking-wide text-[var(--iris-fg-4)]">{label}</div>
      <div
        className={cn(
          "text-[11px] font-medium",
          day ? "text-[var(--iris-fg-1)]" : "text-[var(--iris-fg-4)]",
        )}
      >
        {formatEndpoint(day)}
      </div>
    </div>
  );
}

interface Props {
  draft: DateRange | undefined;
  onSelect: (next: DateRange | undefined) => void;
  /** Injectable "now" so the future-date cutoff is testable; defaults to today. */
  today?: Date;
}

export function RangeCalendar({ draft, onSelect, today = new Date() }: Props) {
  const next = nextEndpoint(draft);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-stretch gap-1.5">
        <Endpoint label="Start" day={draft?.from} active={next === "start"} />
        <Endpoint label="End" day={draft?.to} active={next === "end"} />
      </div>
      {/* IR-1384 r3 (Rotem): no future dates. Every filter this calendar feeds
          asks when something already happened, so a day after today can only
          ever match nothing — offering it invites a range that silently returns
          an empty table. `after` compares whole calendar days, so today itself
          stays selectable whatever the clock says; `endMonth` also stops the
          next-month chevron paging into an all-disabled grid. */}
      <DayPicker
        className="iris-daypicker"
        mode="range"
        selected={draft}
        onSelect={onSelect}
        today={today}
        endMonth={today}
        disabled={{ after: today }}
      />
    </div>
  );
}
